import React, { useState, useRef } from "react";
import { makeStyles } from "@material-ui/styles";
import {
	Grid,
	Card,
	CardHeader,
	CardContent,
	CardActions,
	Avatar,
	Chip,
	Divider,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	RadioGroup,
	Radio,
	FormControl,
	FormControlLabel,
	FormLabel,
	Switch,
	Button,
	Tooltip,
	Backdrop,
	CircularProgress,
} from "@material-ui/core";

import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	Legend as RechartsLegend,
	ResponsiveContainer,
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DateFnsUtils from '@date-io/date-fns';
import {
	MuiPickersUtilsProvider,
	KeyboardDatePicker,
} from '@material-ui/pickers';
import { ThemeProvider as MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from "react-intl";

import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import InsightsIcon from '@mui/icons-material/Insights';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LightbulbIcon from '@mui/icons-material/EmojiObjects';
import BarChartIcon from '@mui/icons-material/BarChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import EventBusyIcon from '@mui/icons-material/EventBusy';

import { SIREN_CONFIG, SIREN_COLOR } from "config.js";

const theme = createMuiTheme({
	overrides: {
		MuiInputBase: {
			input: {
				fontSize: '18px',
			},
		},
	},
});

const TREND_META = {
	up: { labelId: 'ai_trend_up', color: SIREN_COLOR.EMERGENCY_COLOR, Icon: ArrowUpwardIcon },
	down: { labelId: 'ai_trend_down', color: SIREN_COLOR.ACTIVITY_COLOR, Icon: ArrowDownwardIcon },
	flat: { labelId: 'ai_trend_flat', color: '#9e9e9e', Icon: RemoveIcon },
};

const GRADIENT = 'linear-gradient(90deg, #4F46E5 0%, #7C3AED 55%, #06B6D4 100%)';

//일자별 병실별 그래프에서, 병실(시리즈)마다 구분되는 색상을 순서대로 배정한다.
const ROOM_CHART_COLORS = [SIREN_COLOR.EMERGENCY_COLOR, '#7C3AED', '#06B6D4', SIREN_COLOR.CAUTION_COLOR, '#4F46E5'];

/**
 * AI 리포트 화면
 * 기간(기준일 + 조회기간)을 선택하면, 해당 기간의 긴급/주의 요약, 병실별 위험도 랭킹,
 * 시설 전체 활동량/재실률 추이, 규칙 기반 AI 텍스트 인사이트를 조회한다.
 * (PDF 다운로드 / 주간 자동 이메일 발송은 UI만 준비되어 있고, 아직 동작하지 않는다.)
 */
const AiReport = (props) => {

	const classes = useStyles();
	const { formatMessage } = useIntl();
	const facility_id = useSelector((store) => store.user.facility_id);

	//기준일(=조회 종료일 다음날, 즉 어제까지 포함) 기본값: 어제
	const [baseDate, setBaseDate] = useState(new Date((new Date()).valueOf() - (1000 * 60 * 60 * 24)));

	//조회기간: default 7일
	const [selectedPeriod, setSelectedPeriod] = useState(7);

	const [loading, setLoading] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [report, setReport] = useState(null);
	const [queryRange, setQueryRange] = useState(null);
	const [pdfGenerating, setPdfGenerating] = useState(false);
	const [showPdfHeader, setShowPdfHeader] = useState(false);

	const reportContentRef = useRef(null);

	const handleBaseDateChange = (date) => {
		setBaseDate(date);
	};

	const onRadioChange = (event) => {
		if (event.target.value === "1_weeks") {
			setSelectedPeriod(7);
		} else if (event.target.value === "4_weeks") {
			setSelectedPeriod(28);
		} else {
			setSelectedPeriod(14);
		}
	};

	const doSearchData = () => {

		if (!facility_id) return;

		var endDate = moment(baseDate).add(1, 'days').format('YYYY-MM-DD');
		var startDate = moment(baseDate).subtract(selectedPeriod - 1, 'days').format('YYYY-MM-DD');

		var url = `${SIREN_CONFIG.HOSTNAME}/insights/report?facility_id=${facility_id}&startDate=${startDate}&endDate=${endDate}&locale=${localStorage.getItem("locale") || "ko"}`;

		setLoading(true);
		setQueryRange({ startDate, endDate });

		fetch(url, {
			method: "GET",
			headers: { "Content-Type": "application/json" },
		})
			.then((response) => response.json())
			.then((responseData) => {
				if (responseData.resultCode === '000') {
					setReport(responseData.resultData);
				} else {
					alert(formatMessage({ id: 'ai_report_error_result_prefix' }) + (responseData.resultMessage || ''));
					setReport(null);
				}
			})
			.catch((error) => {
				console.error("AI 리포트 조회 오류:", error);
				alert(formatMessage({ id: 'ai_error_fetch_generic' }) + error.message);
				setReport(null);
			})
			.finally(() => {
				setLoading(false);
				setLoaded(true);
			});
	};

	const summary = report && report.summary;

	const changeRateMeta = () => {
		if (!summary || summary.changeRate == null) {
			return { text: formatMessage({ id: 'ai_report_change_no_data' }), Icon: TrendingFlatIcon, color: '#9e9e9e' };
		}
		if (summary.changeRate > 0) {
			return { text: formatMessage({ id: 'ai_report_change_increase' }, { rate: summary.changeRate }), Icon: TrendingUpIcon, color: SIREN_COLOR.EMERGENCY_COLOR };
		}
		if (summary.changeRate < 0) {
			return { text: formatMessage({ id: 'ai_report_change_decrease' }, { rate: Math.abs(summary.changeRate) }), Icon: TrendingDownIcon, color: SIREN_COLOR.ACTIVITY_COLOR };
		}
		return { text: formatMessage({ id: 'ai_report_change_flat' }), Icon: TrendingFlatIcon, color: '#9e9e9e' };
	};

	const dailyRoomChartData = () => {
		if (!report || !report.dailyRoomEmergency || !report.dailyRoomEmergency.rows || report.dailyRoomEmergency.rows.length === 0) return null;

		const { rooms, rows } = report.dailyRoomEmergency;
		if (!rooms || rooms.length === 0) return null;

		const data = rows.map((row) => {
			const entry = { date: moment(row.date).format('MM.DD') };
			rooms.forEach((roomName) => {
				entry[roomName] = row.counts[roomName] || 0;
			});
			return entry;
		});

		return { rooms, data };
	};

	const chartRows = dailyRoomChartData();
	const changeRate = changeRateMeta();

	//누적 막대의 각 날짜별로, 그 날 실제로 맨 위에 그려지는(값이 0이 아닌 마지막) 병실 세그먼트에만
	//둥근 모서리를 준다. (recharts는 나중에 선언된 <Bar>가 스택 위쪽에 그려진다)
	const makeStackedBarShape = (roomName, roomsList, radius) => (barProps) => {
		const { x, y, width, height, payload, fill } = barProps;
		if (!height || height <= 0) return null;

		let topRoom = null;
		for (let i = roomsList.length - 1; i >= 0; i--) {
			if ((payload[roomsList[i]] || 0) > 0) {
				topRoom = roomsList[i];
				break;
			}
		}

		const r = roomName === topRoom ? Math.min(radius, width / 2, height) : 0;

		if (r <= 0) {
			return <rect x={x} y={y} width={width} height={height} fill={fill} />;
		}

		const path = `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} Z`;
		return <path d={path} fill={fill} />;
	};

	const handleDownloadPdf = () => {
		const input = reportContentRef.current;
		if (!input || !queryRange) return;

		setPdfGenerating(true);
		setShowPdfHeader(true);

		//pdfHeader가 화면에 그려질 시간을 준 다음 캡처한다.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				html2canvas(input, { scale: 1.5, useCORS: true, backgroundColor: '#F4F6F8' })
					.then((canvas) => {
						const pdf = new jsPDF('p', 'pt', 'a4');
						const pageWidth = pdf.internal.pageSize.getWidth();
						const pageHeight = pdf.internal.pageSize.getHeight();
						const imgWidth = pageWidth;
						const imgHeight = (canvas.height * imgWidth) / canvas.width;
						const imgData = canvas.toDataURL('image/jpeg', 0.85);

						let heightLeft = imgHeight;
						let position = 0;

						pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
						heightLeft -= pageHeight;

						while (heightLeft > 0) {
							position -= pageHeight;
							pdf.addPage();
							pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
							heightLeft -= pageHeight;
						}

						pdf.save(`AI리포트_${queryRange.startDate}_${queryRange.endDate}.pdf`);
					})
					.catch((error) => {
						console.error("PDF 생성 오류:", error);
						alert(formatMessage({ id: 'ai_report_error_pdf' }) + error.message);
					})
					.finally(() => {
						setShowPdfHeader(false);
						setPdfGenerating(false);
					});
			});
		});
	};

	const StatCard = ({ icon, iconBg, value, label, caption }) => (
		<Card className={classes.statCard}>
			<CardContent className={classes.statCardContent}>
				<Avatar className={classes.statAvatar} style={{ backgroundColor: iconBg }}>
					{icon}
				</Avatar>
				<div className={classes.statText}>
					<Typography variant="h4" className={classes.statValue}>{value}</Typography>
					<Typography variant="body2" className={classes.statLabel}>{label}</Typography>
					{caption && <Typography variant="caption" className={classes.statCaption}>{caption}</Typography>}
				</div>
			</CardContent>
		</Card>
	);

	return (
		<div className={classes.root}>

			<Backdrop className={classes.backdrop} open={loading || pdfGenerating}>
				<div className={classes.backdropContent}>
					<CircularProgress color="inherit" size={56} thickness={4} />
					<Typography variant="body1" className={classes.backdropText}>
						{pdfGenerating ? <FormattedMessage id="ai_report_pdf_generating" /> : <FormattedMessage id="ai_report_loading" />}
					</Typography>
				</div>
			</Backdrop>

			{/* 페이지 헤더 */}
			<div className={classes.pageHeader}>
				<Avatar className={classes.pageHeaderAvatar}><InsightsIcon /></Avatar>
				<div>
					<Typography variant="h5" className={classes.pageHeaderTitle}><FormattedMessage id="ai_report_query" /></Typography>
					<Typography variant="body2" className={classes.pageHeaderSubtitle}>
						<FormattedMessage id="ai_report_page_subtitle" />
					</Typography>
				</div>
			</div>

			{/* 필터 */}
			<Card className={classes.filterCard}>
				<CardContent className={classes.filterContent}>
					<Grid container spacing={2} alignItems="center">

						<Grid item>
							<MuiThemeProvider theme={theme}>
								<MuiPickersUtilsProvider utils={DateFnsUtils}>
									<KeyboardDatePicker
										autoOk
										maxDate={moment()}
										variant="inline"
										label={formatMessage({ id: 'ai_report_base_date' })}
										format="yyyy-MM-dd"
										value={baseDate}
										onChange={handleBaseDateChange}
										KeyboardButtonProps={{ 'aria-label': 'change date' }}
										className={classes.datepicker}
									/>
								</MuiPickersUtilsProvider>
							</MuiThemeProvider>
						</Grid>

						<Grid item>
							<FormControl component="fieldset">
								<Grid container alignItems="center">
									<FormLabel component="legend" className={classes.periodLabel}><FormattedMessage id="healthdata_by_room_query_period" /></FormLabel>
									<RadioGroup row aria-label="ai_report_period" name="ai_report_period" defaultValue="1_weeks" onChange={onRadioChange}>
										<FormControlLabel value="1_weeks" control={<Radio color="primary" />} label={formatMessage({ id: 'healthdata_by_room_query_1week' })} labelPlacement="end" />
										<FormControlLabel value="2_weeks" control={<Radio color="primary" />} label={formatMessage({ id: 'healthdata_by_room_query_2week' })} labelPlacement="end" />
										<FormControlLabel value="4_weeks" control={<Radio color="primary" />} label={formatMessage({ id: 'healthdata_by_room_query_4week' })} labelPlacement="end" />
									</RadioGroup>
								</Grid>
							</FormControl>
						</Grid>

						<Grid item className={classes.searchButtonWrap}>
							<Button
								variant="contained"
								color="primary"
								size="large"
								className={classes.searchButton}
								onClick={doSearchData}
								disabled={loading}
							>
								{loading ? <FormattedMessage id="common_searching" /> : <FormattedMessage id="search" />}
							</Button>
						</Grid>

					</Grid>
				</CardContent>
			</Card>

			{loaded && !report && (
				<Card className={classes.noDataCard}>
					<EventBusyIcon className={classes.noDataIcon} />
					<Typography variant="h6" color="textSecondary"><FormattedMessage id="ai_report_no_data" /></Typography>
				</Card>
			)}

			{report && (
				<>
				<div ref={reportContentRef}>
					{showPdfHeader && (
						<div className={classes.pdfHeader}>
							<Typography variant="h2" className={classes.pdfTitle}><FormattedMessage id="ai_report_pdf_title" /></Typography>
							<Typography variant="h5" className={classes.pdfPeriod}>
								{formatMessage({ id: 'ai_report_pdf_period_prefix' })} {queryRange && moment(queryRange.startDate).format('YYYY.MM.DD')} ~ {queryRange && moment(queryRange.endDate).subtract(1, 'days').format('YYYY.MM.DD')}
							</Typography>
						</div>
					)}

					{/* 요약 카드 */}
					<Grid container spacing={2} className={classes.section}>
						<Grid item xs={12} sm={6} md={3}>
							<StatCard
								icon={<NotificationsActiveIcon />}
								iconBg={SIREN_COLOR.EMERGENCY_BG2_COLOR}
								value={formatMessage({ id: 'ai_unit_count_cases' }, { count: summary.emergencyCount })}
								label={formatMessage({ id: 'ai_report_summary_emergency' })}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<StatCard
								icon={<ReportProblemIcon />}
								iconBg={SIREN_COLOR.CAUTION_BG2_COLOR}
								value={formatMessage({ id: 'ai_unit_count_cases' }, { count: summary.cautionCount })}
								label={formatMessage({ id: 'ai_report_summary_caution' })}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<StatCard
								icon={<changeRate.Icon />}
								iconBg={changeRate.color+'CC'}
								value={changeRate.text}
								label={formatMessage({ id: 'ai_report_summary_change_rate' })}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<StatCard
								icon={<AccessTimeIcon />}
								iconBg="#7C3AEDCC"
								value={summary.avgResponseMinutes != null ? formatMessage({ id: 'ai_unit_minutes' }, { count: summary.avgResponseMinutes }) : formatMessage({ id: 'common_no_data_value' })}
								label={formatMessage({ id: 'ai_report_summary_avg_response' })}
								caption={formatMessage({ id: 'ai_report_summary_avg_response_caption' })}
							/>
						</Grid>
					</Grid>

					<Grid container spacing={2} className={classes.section}>

						{/* 위험도 랭킹 */}
						<Grid item xs={12} md={6}>
							<Card className={classes.sectionCard}>
								<CardHeader
									avatar={<Avatar className={classes.sectionIcon} style={{ backgroundColor: SIREN_COLOR.EMERGENCY_BG2_COLOR }}><WhatshotIcon /></Avatar>}
									title={formatMessage({ id: 'ai_report_risk_ranking_title' })}
									subheader={formatMessage({ id: 'ai_report_risk_ranking_subtitle' })}
									titleTypographyProps={{ variant: 'h6' }}
								/>
								<Divider />
								<CardContent className={classes.tableContent}>
									{report.riskRanking && report.riskRanking.length > 0 ? (
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell><FormattedMessage id="ai_report_col_room" /></TableCell>
													<TableCell align="center"><FormattedMessage id="ai_report_col_risk_score" /></TableCell>
													<TableCell align="center"><FormattedMessage id="ai_report_col_signal" /></TableCell>
													<TableCell align="center"><FormattedMessage id="ai_report_col_last_event" /></TableCell>
													<TableCell align="center"><FormattedMessage id="ai_report_col_trend" /></TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{report.riskRanking.map((room) => {
													const meta = TREND_META[room.trend] || TREND_META.flat;
													const TrendIcon = meta.Icon;
													return (
														<TableRow key={room.roomName} hover>
															<TableCell className={classes.roomNameCell}>{room.roomName}</TableCell>
															<TableCell align="center">
																<span className={classes.scoreBadge}>{room.riskScore}</span>
															</TableCell>
															<TableCell align="center">
																{[
																	room.emergencyCount > 0 ? formatMessage({ id: 'ai_report_emergency_count_label' }, { count: room.emergencyCount }) : null,
																	room.cautionCount > 0 ? formatMessage({ id: 'ai_report_caution_count_label' }, { count: room.cautionCount }) : null,
																].filter(Boolean).join(' · ')}
															</TableCell>
															<TableCell align="center">
																{room.lastEventTime ? moment(room.lastEventTime).format('MM.DD HH:mm') : '-'}
															</TableCell>
															<TableCell align="center">
																<Chip
																	size="small"
																	icon={<TrendIcon style={{ color: meta.color, fontSize: 16 }} />}
																	label={formatMessage({ id: meta.labelId })}
																	className={classes.trendChip}
																	style={{ borderColor: meta.color, color: meta.color }}
																	variant="outlined"
																/>
															</TableCell>
														</TableRow>
													);
												})}
											</TableBody>
										</Table>
									) : (
										<Typography variant="body2" color="textSecondary"><FormattedMessage id="ai_report_risk_ranking_empty" /></Typography>
									)}
								</CardContent>
							</Card>
						</Grid>

						{/* AI 텍스트 인사이트 */}
						<Grid item xs={12} md={6}>
							<Card className={classes.sectionCard}>
								<CardHeader
									avatar={<Avatar className={classes.sectionIcon} style={{ background: GRADIENT }}><LightbulbIcon /></Avatar>}
									title={formatMessage({ id: 'ai_insight_title' })}
									subheader={formatMessage({ id: 'ai_insight_subtitle' })}
									titleTypographyProps={{ variant: 'h6' }}
								/>
								<Divider />
								<CardContent>
									{report.insightSentences && report.insightSentences.length > 0 ? (
										report.insightSentences.map((sentence, idx) => (
											<div key={idx} className={classes.insightRow}>
												<span className={classes.insightBullet} />
												<Typography variant="body1" className={classes.insightSentence}>{sentence}</Typography>
											</div>
										))
									) : (
										<Typography variant="body2" color="textSecondary"><FormattedMessage id="ai_insight_empty" /></Typography>
									)}
								</CardContent>
							</Card>
						</Grid>
					</Grid>

					{/* 일자별 병실별 긴급 발생수 */}
					<Card className={classes.sectionCard + ' ' + classes.section}>
						<CardHeader
							avatar={<Avatar className={classes.sectionIcon} style={{ backgroundColor: '#06B6D4CC' }}><BarChartIcon /></Avatar>}
							title={formatMessage({ id: 'ai_report_chart_title' })}
							subheader={formatMessage({ id: 'ai_report_chart_subtitle' })}
							titleTypographyProps={{ variant: 'h6' }}
						/>
						<Divider />
						<CardContent>
							{chartRows ? (
								<ResponsiveContainer width="100%" height={360}>
									<BarChart data={chartRows.data} margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
										<CartesianGrid strokeDasharray="3 3" vertical={false} />
										<XAxis dataKey="date" tick={{ fontSize: 12 }} />
										<YAxis
											allowDecimals={false}
											tick={{ fontSize: 12 }}
											label={{ value: formatMessage({ id: 'ai_report_chart_yaxis' }), angle: -90, position: 'insideLeft', fontSize: 12 }}
										/>
										<RechartsTooltip />
										<RechartsLegend wrapperStyle={{ fontSize: 12 }} />
										{chartRows.rooms.map((roomName, idx) => (
											<Bar
												key={roomName}
												dataKey={roomName}
												stackId="rooms"
												fill={ROOM_CHART_COLORS[idx % ROOM_CHART_COLORS.length]}
												shape={makeStackedBarShape(roomName, chartRows.rooms, 6)}
											/>
										))}
									</BarChart>
								</ResponsiveContainer>
							) : (
								<Typography variant="body2" color="textSecondary"><FormattedMessage id="ai_report_chart_empty" /></Typography>
							)}
						</CardContent>
					</Card>
				</div>

					{/* 발행/구독 */}
					<Card className={classes.sectionCard + ' ' + classes.section}>
						<CardHeader
							title={formatMessage({ id: 'ai_report_publish_title' })}
							subheader={formatMessage({ id: 'ai_report_publish_subtitle' })}
							titleTypographyProps={{ variant: 'h6' }}
						/>
						<Divider />
						<CardActions className={classes.publishActions}>
							<Button
								variant="contained"
								color="primary"
								startIcon={<PictureAsPdfIcon />}
								onClick={handleDownloadPdf}
								disabled={pdfGenerating}
								className={classes.publishButton}
							>
								{pdfGenerating ? <FormattedMessage id="ai_report_pdf_generating_btn" /> : <FormattedMessage id="ai_report_pdf_download_btn" />}
							</Button>

							<Tooltip title={formatMessage({ id: 'ai_report_coming_soon' })}>
								<div className={classes.emailToggle}>
									<MailOutlineIcon className={classes.emailIcon} />
									<FormControlLabel
										control={<Switch color="primary" disabled />}
										label={formatMessage({ id: 'ai_report_weekly_email' })}
									/>
								</div>
							</Tooltip>
						</CardActions>
					</Card>
				</>
			)}

		</div>
	);
};

export default AiReport;

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
		backgroundColor: theme.palette.background.default,
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
	backdropContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: theme.spacing(2),
	},
	backdropText: {
		color: '#fff',
	},
	pageHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		background: GRADIENT,
		borderRadius: 12,
		padding: theme.spacing(2, 3),
		marginBottom: theme.spacing(2),
		boxShadow: '0 4px 14px rgba(79, 70, 229, 0.25)',
	},
	pageHeaderAvatar: {
		backgroundColor: 'rgba(255,255,255,0.2)',
		width: 48,
		height: 48,
	},
	pageHeaderTitle: {
		color: '#fff',
		fontWeight: 700,
	},
	pageHeaderSubtitle: {
		color: 'rgba(255,255,255,0.85)',
	},
	filterCard: {
		marginBottom: theme.spacing(1),
		borderRadius: 12,
		boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
	},
	filterContent: {
		padding: theme.spacing(2, 3),
	},
	periodLabel: {
		marginRight: theme.spacing(2),
		fontWeight: 600,
	},
	searchButtonWrap: {
		marginLeft: 'auto',
	},
	searchButton: {
		borderRadius: 8,
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
		boxShadow: 'none',
	},
	section: {
		marginTop: theme.spacing(3),
	},
	pdfHeader: {
		textAlign: 'center',
		paddingTop: theme.spacing(6),
		marginBottom: theme.spacing(2),
	},
	pdfTitle: {
		fontWeight: 700,
		color: theme.palette.text.primary,
	},
	pdfPeriod: {
		marginTop: theme.spacing(1),
		color: theme.palette.text.secondary,
		fontWeight: 500,
	},
	statCard: {
		height: '100%',
		borderRadius: 12,
		boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
		transition: 'box-shadow 0.2s ease, transform 0.2s ease',
		'&:hover': {
			boxShadow: '0 6px 16px rgba(0,0,0,0.10)',
			transform: 'translateY(-2px)',
		},
	},
	statCardContent: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
	},
	statAvatar: {
		width: 52,
		height: 52,
		color: '#fff',
	},
	statText: {
		minWidth: 0,
	},
	statValue: {
		fontWeight: 700,
		lineHeight: 1.2,
	},
	statLabel: {
		color: theme.palette.text.secondary,
	},
	statCaption: {
		color: theme.palette.text.hint || theme.palette.text.secondary,
	},
	sectionCard: {
		borderRadius: 12,
		boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
		height: '100%',
	},
	sectionIcon: {
		color: '#fff',
	},
	tableContent: {
		overflowX: 'auto',
	},
	roomNameCell: {
		fontWeight: 600,
	},
	scoreBadge: {
		display: 'inline-block',
		minWidth: 28,
		padding: '2px 10px',
		borderRadius: 12,
		fontWeight: 700,
		fontSize: 13,
		backgroundColor: '#EEF2FF',
		color: '#4F46E5',
	},
	trendChip: {
		fontWeight: 600,
	},
	insightRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: theme.spacing(1.5),
		padding: theme.spacing(1, 1.5),
		borderRadius: 8,
		marginBottom: theme.spacing(1),
		backgroundColor: 'rgba(79, 70, 229, 0.05)',
	},
	insightBullet: {
		width: 8,
		height: 8,
		borderRadius: '50%',
		background: GRADIENT,
		marginTop: 7,
		flexShrink: 0,
	},
	insightSentence: {
		lineHeight: 1.6,
	},
	publishActions: {
		padding: theme.spacing(2, 3),
		flexWrap: 'wrap',
		gap: theme.spacing(2),
	},
	publishButton: {
		borderRadius: 8,
	},
	emailToggle: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginLeft: theme.spacing(1),
	},
	emailIcon: {
		color: theme.palette.text.secondary,
	},
	datepicker: {
		width: 160,
	},
	noDataCard: {
		marginTop: theme.spacing(3),
		padding: theme.spacing(6),
		textAlign: 'center',
		borderRadius: 12,
		boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
	},
	noDataIcon: {
		fontSize: 48,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1),
	},
}));
